import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getProducts, updateProduct } from '../../../services/ProductsService';
import { toast } from 'react-toastify';
import { ProductStatusBadge, ProductStatusChips, countByStatus, matchesStatusFilter } from './ProductStatus';

export default function ProductStock() {
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [togglingTraceableId, setTogglingTraceableId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const statusCounts = useMemo(() => countByStatus(products), [products]);

    const load = () => {
        getProducts()
            .then((res) => setProducts(res.data || []))
            .catch(() => toast.error(t('productStock.errors.load')))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleQuantityChange = (productId, value) => {
        setProducts((prev) =>
            prev.map((p) =>
                p.id === productId
                    ? { ...p, quantity_available: value === '' ? '' : Math.max(0, parseFloat(value) || 0) }
                    : p
            )
        );
    };

    const handleQuantityBlur = async (p) => {
        const qty = p.quantity_available;
        const num = typeof qty === 'number' ? qty : parseFloat(qty);
        if (isNaN(num) || num < 0) {
            handleQuantityChange(p.id, 0);
        }
        setUpdatingId(p.id);
        try {
            await updateProduct(p.id, { quantity_available: typeof num === 'number' ? num : (parseFloat(p.quantity_available) || 0) });
            load();
        } catch {
            toast.error(t('productStock.errors.updateQuantity'));
        } finally {
            setUpdatingId(null);
        }
    };

    const handleTraceableToggle = async (p) => {
        setTogglingTraceableId(p.id);
        try {
            await updateProduct(p.id, { traceable: !p.traceable });
            load();
        } catch {
            toast.error(t('productStock.errors.updateTraceable'));
        } finally {
            setTogglingTraceableId(null);
        }
    };

    const searchLower = (searchQuery || '').trim().toLowerCase();
    const byStatus = products.filter((p) => matchesStatusFilter(p, statusFilter));
    const filteredProducts = searchLower
        ? byStatus.filter((p) => (p.name || '').toLowerCase().includes(searchLower))
        : byStatus;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div>
                                <h4 className="heading mb-0">{t('productStock.title')}</h4>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Button variant="light" size="sm" as={Link} to="/products">
                                    <i className="fa-solid fa-box me-1" /> {t('productStock.productsButton')}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="product-filter-row">
                                <ProductStatusChips value={statusFilter} onChange={setStatusFilter} counts={statusCounts} />
                                <Form.Control
                                    type="search"
                                    placeholder={t('productStock.searchPlaceholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="form-control-sm products-search-bar"
                                    aria-label={t('productStock.searchAriaLabel')}
                                />
                            </div>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                </div>
                            ) : (
                                <Table responsive hover className="table-products-stock table-products-stock--clean" style={{ tableLayout: 'fixed', width: '100%' }}>
                                    <colgroup>
                                        <col style={{ width: '38%' }} />
                                        <col style={{ width: '20%' }} />
                                        <col style={{ width: '20%' }} />
                                        <col style={{ width: '22%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th className="py-2">{t('productStock.columns.product')}</th>
                                            <th className="text-center py-2">{t('productStock.columns.quantityAvailable')}</th>
                                            <th className="text-center py-2">{t('productStock.columns.traceable')}</th>
                                            <th className="text-center py-2">{t('productStock.columns.status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5 text-muted">
                                                    {t('productStock.noProducts')} <Link to="/products">{t('productStock.addProductsLink')}</Link>
                                                </td>
                                            </tr>
                                        ) : filteredProducts.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5 text-muted">
                                                    {t('productStock.noSearchMatch')}
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredProducts.map((p) => (
                                                <tr key={p.id}>
                                                    <td className="py-2 align-middle product-name-cell"><strong>{p.name}</strong>{!p.is_delivery && <ProductStatusBadge status={p.sales_status} />}</td>
                                                    <td className="text-center py-2 align-middle">
                                                        <Form.Control
                                                            type="number"
                                                            min={0}
                                                            step={1}
                                                            size="sm"
                                                            value={p.quantity_available ?? ''}
                                                            onChange={(e) => handleQuantityChange(p.id, e.target.value)}
                                                            onBlur={() => handleQuantityBlur(p)}
                                                            disabled={updatingId === p.id}
                                                            className="d-inline-block mx-auto"
                                                            style={{ width: 96 }}
                                                        />
                                                    </td>
                                                    <td className="text-center py-2 align-middle">
                                                        <Form.Check
                                                            type="switch"
                                                            id={`traceable-stock-${p.id}`}
                                                            checked={Boolean(p.traceable)}
                                                            disabled={togglingTraceableId === p.id}
                                                            onChange={() => handleTraceableToggle(p)}
                                                            label={p.traceable ? t('common.yes') : t('common.no')}
                                                            className="d-inline-flex align-items-center justify-content-center"
                                                        />
                                                    </td>
                                                    <td className="text-center py-2 align-middle">
                                                        {p.traceable && (parseFloat(p.quantity_available) || 0) <= 0 ? (
                                                            <span className="text-danger fw-semibold">{t('productStock.outOfStock')}</span>
                                                        ) : (parseFloat(p.quantity_available) || 0) > 0 ? (
                                                            <span className="text-success">{t('productStock.available')}</span>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}
